import { useCallback, useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, Alert, ErrorAlert, Icon, PageHeader, Spinner, useAsyncAction, withStepUp, type IconName } from '@pryme/ui';
import { ActivationPanel, activationBlockers } from '../components/ActivationPanel';
import { ErrorBox } from '../components/ErrorBox';
import { KeyDiagram } from '../components/KeyDiagram';
import { OneTimeSecretPanel } from '../components/OneTimeSecretPanel';
import { RedirectEditor } from '../components/RedirectEditor';
import { ServiceEnvBadge } from '../components/ServiceLayout';
import { useDev } from '../lib/context';
import { canAdmin, canDevelop, ROLE_LABEL, type CredentialPanel, type Service } from '../lib/types';

const STEPS = ['Organization', 'Name', 'Redirect URIs', 'Credentials', 'Activation', 'Done'] as const;

const STATUS_TEXT: Record<string, string> = { pending_credentials: 'waiting for credentials', pending_activation: 'waiting for activation', suspended: 'suspended', revoked: 'revoked' };

const NEXT: { path: string; label: string; text: string; icon: IconName }[] = [
  { path: 'sdk', label: 'Integrate the SDK', text: 'Snippets for Express, Workers and plain HTTP with this service’s identifiers filled in.', icon: 'arrowRight' },
  { path: 'claims', label: 'Choose the claims you request', text: 'For example age.over18. People approve each claim on the consent screen.', icon: 'arrowRight' },
  { path: 'playground', label: 'Send a signed request', text: 'See the signature base, Pryme’s verification and the signed response.', icon: 'arrowRight' },
  { path: 'mandates', label: 'Test agent mandates', text: 'Run an agent’s request through the online authorization check, including approvals.', icon: 'arrowRight' },
  { path: 'events', label: 'Watch live events', text: 'Logins, signatures, replays and mandate decisions as they happen. No personal data.', icon: 'arrowRight' },
];

export function CreateService() {
  const [params, setParams] = useSearchParams();
  const step = Math.min(Math.max(Number(params.get('step') ?? 0), 0), STEPS.length - 1);
  const serviceId = params.get('service');
  const { orgs, org, selectOrg, reloadServices, environment: deployment } = useDev();
  const production = deployment === 'production';
  const [service, setService] = useState<Service | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [panel, setPanel] = useState<CredentialPanel | null>(null);
  const [memorySecret, setMemorySecret] = useState('');
  const go = useCallback(
    (n: number, svc?: string) => setParams((p) => {
      const q = new URLSearchParams(p);
      q.set('step', String(n));
      if (svc) q.set('service', svc);
      return q;
    }),
    [setParams],
  );

  const loadService = useCallback(async () => {
    if (!serviceId) return;
    try {
      setService(await api<Service>(`/v1/services/${serviceId}`));
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    }
  }, [serviceId]);
  useEffect(() => {
    void loadService();
  }, [loadService]);

  // ----- step 1 form state
  const nameId = useId();
  const [name, setName] = useState('');
  const create = useAsyncAction();
  const creds = useAsyncAction();

  const needsService = step >= 2 && !service;

  return (
    <>
      <PageHeader
        eyebrow={`New service · ${production ? 'Production' : 'Sandbox'}`}
        title="Register a service"
        lead="A service is a website or app that signs people in with Pryme or accepts requests from their agents. Registration takes five steps; you can leave and continue later from the dashboard."
      />
      <ol className="p-steps" aria-label="Wizard progress">
        {STEPS.map((s, i) => (
          <li key={s} className={i < step ? 'done' : i === step ? 'current' : ''} aria-current={i === step ? 'step' : undefined}>
            {s}
          </li>
        ))}
      </ol>
      {service && (
        <p className="p-row p-small" style={{ gap: '0.5rem', marginBottom: '1rem' }}>
          <strong>{service.displayName}</strong> <ServiceEnvBadge environment={service.environment} /> <code className="p-mono-chip">{service.id}</code>
        </p>
      )}
      <ErrorAlert error={loadError} />
      {needsService && !loadError && (serviceId ? <Spinner label="Loading service…" /> : <Alert tone="warn">No service selected. <button className="p-btn small secondary" onClick={() => go(0)}>Start over</button></Alert>)}

      {step === 0 && (
        <section className="p-card">
          <h2>Choose the organization</h2>
          <p className="p-small p-muted">The service belongs to an organization. You need at least the Developer role there.</p>
          {orgs.length === 0 ? (
            <Alert tone="info">Create an organization first using the organization selector in the navigation.</Alert>
          ) : (
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="p-skip">Organization</legend>
              {orgs.map((o) => (
                <label key={o.id} className="p-check">
                  <input type="radio" name="org" checked={org?.id === o.id} onChange={() => selectOrg(o.id)} disabled={!canDevelop(o.role)} />
                  <span>{o.display_name} <span className="p-muted p-small">· {ROLE_LABEL[o.role]}{!canDevelop(o.role) ? ' (read-only — cannot create services)' : ''}</span></span>
                </label>
              ))}
            </fieldset>
          )}
          <button className="p-btn" disabled={!org || !canDevelop(org.role)} onClick={() => go(1)}>Continue</button>
        </section>
      )}

      {step === 1 && (
        <section className="p-card">
          <h2>Name the service</h2>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!org) return;
              const r = await create.run(() => api<{ id: string }>('/v1/services', { body: { organizationId: org.id, displayName: name.trim() } }));
              if (r) {
                await reloadServices();
                go(2, r.id);
              }
            }}
          >
            <div className="p-field">
              <label htmlFor={nameId}>Display name</label>
              <input id={nameId} className="p-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required placeholder="Acme Shop" />
              <span className="p-hint">Shown to people on the Pryme consent screen and in their list of connected services.</span>
            </div>
            {production ? (
              <Alert tone="info">
                This is a production service. It accepts people verified in the production trust environment only, and its redirect URIs must be on a verified domain of{' '}
                {org?.display_name ?? 'your organization'}.
              </Alert>
            ) : (
              <Alert tone="info">
                This is a sandbox service. It accepts test identities only (Idura MitID test users and the offline test provider), has its own trust root and needs no
                domain. To register a production service, switch to Production in the top bar.
              </Alert>
            )}
            <ErrorAlert error={create.error} />
            <div className="p-row">
              <button type="button" className="p-btn secondary" onClick={() => go(0)}>Back</button>
              <button className="p-btn" disabled={create.busy || !name.trim()}>{create.busy ? <Spinner label="Creating…" /> : 'Create service'}</button>
            </div>
          </form>
        </section>
      )}

      {step === 2 && service && (
        <section className="p-card">
          <h2>Redirect URIs</h2>
          <p className="p-small p-muted">After sign-in, Pryme returns the browser only to a URI registered here, matched exactly (RFC 9700 open-redirect protection).</p>
          <RedirectEditor service={service} canEdit={canAdmin(service.role)} onSaved={() => void loadService()} />
          <hr className="p-divider" />
          <div className="p-row">
            <button className="p-btn" disabled={service.redirectUris.length === 0} onClick={() => go(3)}>Continue</button>
          </div>
        </section>
      )}

      {step === 3 && service && (
        <section className="p-card">
          <h2>Credentials</h2>
          {panel ? (
            <OneTimeSecretPanel
              panel={panel}
              doneLabel="Continue to activation"
              onAcknowledged={() => {
                setMemorySecret(panel.secretKey);
                setPanel(null);
                void loadService();
                go(4);
              }}
            />
          ) : service.status !== 'pending_credentials' ? (
            <>
              <Alert tone="info">Credentials were already issued for this service. Use rotation on the Credentials page if you lost the secret.</Alert>
              <button className="p-btn" onClick={() => go(4)}>Continue</button>
            </>
          ) : (
            <>
              <KeyDiagram />
              <p className="p-small" style={{ marginTop: '1rem' }}>
                Pryme issues one secret key for your backend: it signs your requests and lets the SDK verify Pryme's responses. It is shown <strong>once</strong>. Generating is confirmed with your passkey.
              </p>
              <ErrorBox error={creds.error} />
              <div className="p-row">
                <button className="p-btn secondary" onClick={() => go(2)}>Back</button>
                <button
                  className="p-btn"
                  disabled={creds.busy || !canAdmin(service.role)}
                  onClick={async () => {
                    const r = await creds.run(() => withStepUp<CredentialPanel>(`service.credentials:${service.id}`, `/v1/services/${service.id}/credentials`));
                    if (r) setPanel(r);
                  }}
                >
                  {creds.busy ? <Spinner label="Confirm with your passkey…" /> : 'Generate credentials'}
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {step === 4 && service && (
        <section className="p-card">
          <h2>Activation</h2>
          {memorySecret && service.status === 'pending_activation' && (
            <Alert tone="info">Your new secret key is held in this tab’s memory only, so you can sign the activation challenge here. It is discarded when you leave this page.</Alert>
          )}
          <ActivationPanel
            service={service}
            initialSecret={memorySecret}
            onActivated={() => {
              setMemorySecret('');
              void loadService();
              void reloadServices();
            }}
          />
          {activationBlockers(service).length > 0 && service.status !== 'active' && (
            <div className="p-row" style={{ marginTop: '0.75rem' }}>
              {service.redirectUris.length === 0 && <button className="p-btn secondary small" onClick={() => go(2)}>Add redirect URIs</button>}
              {service.environment === 'production' && service.redirectUris.length > 0 && !service.domain && <Link className="p-btn secondary small" to="/domains">Verify a domain</Link>}
            </div>
          )}
          <hr className="p-divider" />
          <button className="p-btn" disabled={service.status !== 'active'} onClick={() => go(5)}>Continue</button>
        </section>
      )}

      {step === 5 && service && (
        <section className="p-card" aria-labelledby="done-h">
          <div className="dv-done">
            <span className="dv-done-icon"><Icon name={service.status === 'active' ? 'check' : 'clock'} size={20} /></span>
            <div>
              <h2 id="done-h" style={{ marginBottom: '0.25rem' }}>{service.displayName} is {service.status === 'active' ? 'active' : STATUS_TEXT[service.status] ?? service.status}</h2>
              <p className="p-small p-muted" style={{ margin: 0 }}>
                {service.status === 'active'
                  ? `It now accepts signed requests and “Continue with Pryme” sign-ins from ${service.environment === 'production' ? 'verified people' : 'sandbox test identities'}.`
                  : 'Finish the remaining steps from the service overview.'}
              </p>
            </div>
          </div>
          <ul className="dv-next" aria-label="Next steps">
            {NEXT.map((n) => (
              <li key={n.path}>
                <Link to={`/services/${service.id}/${n.path}`}>
                  <strong>{n.label}</strong>
                  <span>{n.text}</span>
                  <Icon name={n.icon} size={16} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
